import { PushNudge } from "../components/PushNudge";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import RegisterSaleModal from "../components/RegisterSaleModal";
import RegisterLeadModal from "../components/RegisterLeadModal";
import RegisterSpendModal from "../components/RegisterSpendModal";
import CounselorCard from "../components/CounselorCard";
import LessonTrailCard from "../components/LessonTrailCard";
import {
  User as UserIcon,
  Flag,
  Target,
  MessageCircle,
  Bot,
  Film,
  FlaskConical,
  TrendingUp,
  Rocket,
  CheckCircle2,
  Circle,
  Lock,
  DoorOpen,
  DoorClosed,
  Flame,
  Zap,
  DollarSign,
  MessageSquare,
  ChevronRight,
  TestTube,
  PlayCircle,
  Activity,
  AlertOctagon,
} from "lucide-react";

const ICON_MAP = {
  user: UserIcon,
  flag: Flag,
  target: Target,
  "message-circle": MessageCircle,
  bot: Bot,
  film: Film,
  "test-tube": TestTube,
  "flask-conical": FlaskConical,
  "trending-up": TrendingUp,
  rocket: Rocket,
};

const STATUS_STYLE = {
  completed: {
    ring: "border-emerald-500/70 bg-emerald-500/15",
    icon: "text-emerald-400",
    label: "Concluído",
    labelClass: "text-emerald-400",
  },
  current: {
    ring: "border-[#FF6B00] bg-[#FF6B00]/15 shadow-[0_0_25px_rgba(255,107,0,0.35)]",
    icon: "text-[#FF6B00]",
    label: "Você está aqui",
    labelClass: "text-[#FF6B00]",
  },
  next: {
    ring: "border-[#FF6B00]/40 bg-[#FF6B00]/5",
    icon: "text-[#FF6B00]/80",
    label: "Próximo",
    labelClass: "text-[#FF6B00]/80",
  },
  locked: {
    ring: "border-[#282D3D] bg-[#1A1D26]",
    icon: "text-gray-600",
    label: "Bloqueado",
    labelClass: "text-gray-500",
  },
};

function StepNode({ step, isFirst, isLast, onToggleItem, saving, expanded, onExpand, onOpenVideo }) {
  const Icon = ICON_MAP[step.icon] || Flag;
  const style = STATUS_STYLE[step.status] || STATUS_STYLE.locked;
  const isLocked = step.status === "locked";
  const isCurrent = step.status === "current";
  const totalItems = (step.checklist || []).length;
  const doneItems = step.checklist_completed_count || 0;
  const percent = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  return (
    <div className="relative flex gap-3 sm:gap-4" data-testid={`journey-step-${step.key_slug}`}>
      {/* Vertical connector line */}
      <div className="flex flex-col items-center shrink-0" aria-hidden>
        {!isFirst && <div className="w-0.5 h-3 bg-[#282D3D]" />}
        <div
          className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 flex items-center justify-center transition-all ${style.ring}`}
        >
          {isLocked ? (
            <Lock className={`w-5 h-5 ${style.icon}`} />
          ) : step.status === "completed" ? (
            <CheckCircle2 className={`w-6 h-6 ${style.icon}`} />
          ) : (
            <Icon className={`w-6 h-6 ${style.icon}`} />
          )}
        </div>
        {!isLast && <div className="w-0.5 flex-1 min-h-8 bg-[#282D3D]" />}
      </div>

      {/* Step card */}
      <div className="flex-1 pb-6">
        <button
          type="button"
          disabled={isLocked}
          onClick={() => (isLocked ? null : onExpand(step.id))}
          data-testid={`journey-step-toggle-${step.key_slug}`}
          className={`w-full text-left rounded-xl border p-3 sm:p-4 transition-all ${
            isCurrent
              ? "border-[#FF6B00]/60 bg-[#1A1D26]"
              : "border-[#282D3D] bg-[#14161C]"
          } ${isLocked ? "opacity-70 cursor-not-allowed" : "hover:border-[#FF6B00]/40"}`}
        >
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <span
                className={`text-[10px] font-extrabold uppercase tracking-wider ${style.labelClass}`}
              >
                {style.label}
              </span>
              {step.mode === "live" && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-purple-300 bg-purple-500/10 border border-purple-500/30 rounded px-1.5 py-0.5">
                  Modo Live
                </span>
              )}
            </div>
            {!isLocked && totalItems > 0 && (
              <span className="text-[10px] font-bold text-gray-400">
                {doneItems}/{totalItems}
              </span>
            )}
          </div>

          <h3 className="text-base sm:text-lg font-extrabold text-white leading-tight">
            {step.name}
          </h3>

          {step.description && !isLocked && (
            <p className="text-xs text-gray-400 mt-1 leading-snug">{step.description}</p>
          )}

          {!isLocked && totalItems > 0 && (
            <div className="mt-2 h-1.5 bg-[#0F1219] rounded-full overflow-hidden border border-[#282D3D]">
              <div
                className={`h-full rounded-full ${
                  step.status === "completed" ? "bg-emerald-500" : "bg-[#FF6B00]"
                }`}
                style={{ width: `${percent}%` }}
              />
            </div>
          )}
        </button>

        {/* Inline expanded checklist (only for unlocked & has items) */}
        {expanded && !isLocked && (
          <>
            {step.instruction_video_enabled && (step.instruction_video_url || step.instruction_video_storage_path) && (
              <button
                type="button"
                onClick={() => onOpenVideo(step)}
                data-testid={`journey-video-cta-${step.key_slug}`}
                className="mt-2 w-full flex items-center gap-2 rounded-xl border border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/15 p-3 text-left transition-colors"
              >
                <span className="w-10 h-10 rounded-lg bg-purple-500/30 flex items-center justify-center shrink-0">
                  <PlayCircle className="w-5 h-5 text-purple-100" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[10px] uppercase tracking-widest font-extrabold text-purple-200">
                    🎥 Conteúdo desta etapa
                  </span>
                  <span className="block text-sm font-bold text-white truncate">
                    {step.instruction_video_title || "Assistir vídeo explicativo"}
                  </span>
                  {step.instruction_video_description && (
                    <span className="block text-[11px] text-purple-200/80 truncate">
                      {step.instruction_video_description}
                    </span>
                  )}
                </span>
                <span className="text-[10px] uppercase tracking-widest font-extrabold text-purple-200 shrink-0">
                  Assistir
                </span>
              </button>
            )}
            {(step.checklist || []).length > 0 && (
              <div
                className="mt-2 rounded-xl border border-[#282D3D] bg-[#0F1219] p-3 space-y-2"
                data-testid={`journey-step-checklist-${step.key_slug}`}
              >
                {step.checklist.map((item) => {
                  const checked = !!(step.checklist_state || {})[item.id];
                  return (
                    <label
                      key={item.id}
                      className={`flex items-start gap-2 cursor-pointer p-2 rounded-lg transition-colors ${
                        checked ? "bg-emerald-500/5" : "hover:bg-[#161923]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={saving}
                        onChange={(e) => onToggleItem(step.id, item.id, e.target.checked)}
                        data-testid={`journey-item-${step.key_slug}-${item.id}`}
                        className="mt-0.5 w-4 h-4 accent-[#FF6B00] cursor-pointer"
                      />
                      <span
                        className={`text-xs ${
                          checked ? "text-gray-500 line-through" : "text-gray-200"
                        }`}
                      >
                        {item.label}
                        {!item.required && (
                          <span className="text-[9px] text-gray-500 ml-1">(opcional)</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Portal({ portal }) {
  const unlocked = portal?.unlocked;
  const crossed = portal?.crossed;
  return (
    <div className="relative flex gap-3 sm:gap-4" data-testid="journey-portal">
      <div className="flex flex-col items-center shrink-0" aria-hidden>
        <div className="w-0.5 h-3 bg-[#282D3D]" />
        <div
          className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border-2 flex items-center justify-center transition-all ${
            crossed
              ? "border-emerald-500/70 bg-emerald-500/15"
              : unlocked
              ? "border-purple-400/80 bg-purple-500/15 shadow-[0_0_30px_rgba(168,85,247,0.45)] animate-pulse"
              : "border-[#282D3D] bg-[#1A1D26]"
          }`}
        >
          {unlocked || crossed ? (
            <DoorOpen
              className={`w-7 h-7 ${
                crossed ? "text-emerald-400" : "text-purple-300"
              }`}
            />
          ) : (
            <DoorClosed className="w-6 h-6 text-gray-600" />
          )}
        </div>
        <div className="w-0.5 flex-1 min-h-8 bg-[#282D3D]" />
      </div>

      <div className="flex-1 pb-6">
        <div
          className={`rounded-xl border p-3 sm:p-4 ${
            crossed
              ? "border-emerald-500/30 bg-emerald-500/5"
              : unlocked
              ? "border-purple-400/40 bg-purple-500/5"
              : "border-[#282D3D] bg-[#14161C]"
          }`}
        >
          <span
            className={`text-[10px] font-extrabold uppercase tracking-wider ${
              crossed
                ? "text-emerald-400"
                : unlocked
                ? "text-purple-300"
                : "text-gray-500"
            }`}
          >
            Portal Caverna → Live
          </span>
          <h3 className="text-base sm:text-lg font-extrabold text-white mt-0.5 leading-tight">
            {crossed
              ? "Você atravessou o portal"
              : unlocked
              ? "Portal do Modo Live liberado"
              : "Portal trancado"}
          </h3>
          <p className="text-xs text-gray-400 mt-1 leading-snug">
            {crossed ? portal.message_unlocked : unlocked ? portal.message_unlocked : portal.message_locked}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function JourneyMapPage() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSpendModal, setShowSpendModal] = useState(false);
  const [videoStep, setVideoStep] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [journeyRes, dashRes] = await Promise.all([
        api.get("/api/mentorship/journey/state"),
        api.get("/api/operations/dashboard").catch(() => ({ data: null })),
      ]);
      setState(journeyRes.data);
      setDashboard(dashRes.data);
      // Auto-expand current step if nothing selected yet.
      const currentId = journeyRes.data?.current_step_id;
      setExpandedId((prev) => prev || currentId);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Não foi possível carregar sua jornada."
      );
    } finally {
      setLoading(false);
    }
    // Deps intentionally empty: `api` is a stable module import; all
    // other references (`currentId`, `journeyRes`) are locals declared
    // inside; React setters are stable. Keeping deps=[] prevents the
    // 30s polling interval from constantly tearing down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchAll();
    };
    const onOnline = () => fetchAll();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [user, fetchAll]);

  // First-active welcome. Shows once per user+mentorship after transition
  // from pending → active is observed on the client.
  const [showWelcome, setShowWelcome] = useState(false);
  useEffect(() => {
    if (!state || state.access_denied || !user) return;
    const mid = state.mentorship_id;
    if (!mid) return;
    // localStorage justification: this is a non-sensitive, per-browser
    // UI flag that prevents the welcome banner from re-appearing after
    // the student has acknowledged it once. No tokens, PII or auth state
    // are stored here. If the user clears storage, the banner simply
    // shows again next visit — zero security impact.
    const key = `mc_welcome_${user.id}_${mid}`;
    try {
      if (!localStorage.getItem(key)) {
        setShowWelcome(true);
        localStorage.setItem(key, new Date().toISOString());
      }
    } catch (storageErr) {
      // Private-browsing / quota-exceeded — show once, don't persist.
      console.warn("welcome flag storage unavailable:", storageErr);
      setShowWelcome(true);
    }
  }, [state, user]);

  const handleToggle = async (stepId, itemId, checked) => {
    setSaving(true);
    try {
      await api.post(
        `/api/mentorship/journey/steps/${stepId}/checklist/${itemId}/toggle`,
        { checked }
      );
      await fetchAll();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erro ao atualizar tarefa.");
    } finally {
      setSaving(false);
    }
  };

  const operation = dashboard?.operation || {};
  const campaign = dashboard?.campaign;

  const currentStep = useMemo(
    () => (state?.steps || []).find((s) => s.status === "current"),
    [state]
  );

  const mode = operation?.mode || "MODO_CAVERNA";
  const isLive = mode === "MODO_LIVE";

  // Health bar interpretation (server is the source of truth; UI just labels ranges).
  const healthScore = Math.max(0, Math.min(100, Number(operation?.health_score ?? 0)));
  const healthLabel =
    healthScore >= 81
      ? { icon: "💰", label: "Forte", tone: "text-emerald-300", bar: "bg-emerald-400" }
      : healthScore >= 61
      ? { icon: "🟡", label: "Saudável", tone: "text-yellow-200", bar: "bg-yellow-400" }
      : healthScore >= 41
      ? { icon: "🟠", label: "Atenção", tone: "text-orange-300", bar: "bg-orange-400" }
      : healthScore >= 21
      ? { icon: "🔴", label: "Fraco", tone: "text-red-300", bar: "bg-red-500" }
      : { icon: "🚨", label: "Crítico", tone: "text-red-400", bar: "bg-red-600" };
  const healthDescription =
    healthScore >= 61
      ? "Operação saudável — mantenha o ritmo de vendas e testes."
      : healthScore >= 41
      ? "Requer atenção — reative campanhas ou registre vendas recentes."
      : "Ação urgente — sua operação está esfriando.";

  if (loading && !state) {
    return (
      <div className="flex items-center justify-center min-h-[70vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-semibold text-gray-300">
            Carregando sua jornada...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto p-6 mt-8 text-center">
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 text-sm text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (state?.access_denied) {
    const reason = state.reason;
    const eff = state.effective_access || {};
    const messages = {
      pending: {
        title: "Aguardando aprovação",
        body: "Seu acesso está aguardando aprovação. Fale com seu mentor para liberar sua jornada.",
      },
      expired: {
        title: "Acesso expirado",
        body: "Seu acesso a esta mentoria expirou. Fale com seu mentor para renovar o acesso.",
      },
      blocked: {
        title: "Acesso bloqueado",
        body: "Seu acesso a esta mentoria está bloqueado. Fale com seu mentor.",
      },
      no_membership: {
        title: "Sem mentoria vinculada",
        body: "Nenhuma mentoria ativa vinculada à sua conta. Peça um código ao seu mentor.",
      },
    };
    const m = messages[reason] || messages.no_membership;
    return (
      <div
        data-testid="journey-access-denied"
        className="max-w-lg mx-auto p-6 mt-8 text-center"
      >
        <div className="bg-[#14161C] border border-[#282D3D] rounded-2xl p-6">
          <AlertOctagon className="w-10 h-10 text-[#FF6B00] mx-auto mb-3" />
          <h2 className="text-lg font-extrabold text-white mb-2">{m.title}</h2>
          <p className="text-sm text-gray-300 mb-3">{m.body}</p>
          {eff.access_expires_at && (
            <p className="text-[11px] text-gray-500 mb-3">
              Expirado em {new Date(eff.access_expires_at).toLocaleDateString("pt-BR")}
            </p>
          )}
          <button
            data-testid="journey-access-recheck"
            onClick={fetchAll}
            className="px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
          >
            Verificar acesso
          </button>
        </div>
      </div>
    );
  }

  const steps = state?.steps || [];
  const cavernaSteps = steps.filter((s) => s.mode === "caverna");
  const liveSteps = steps.filter((s) => s.mode === "live");
  const firstName = user?.name?.split(" ")[0] || "Operador";

  return (
    <div
      data-testid="journey-map-container"
      className="max-w-xl mx-auto px-3 sm:px-4 pt-4 pb-24"
    >
      {user?.role === "student" && <PushNudge />}
      {/* Operation Health — primary metric on Home */}
      <div
        data-testid="operation-health-card"
        className="rounded-xl border border-[#282D3D] bg-[#14161C] p-4 mb-3"
      >
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#FF6B00]" />
            <span className="text-[10px] uppercase tracking-widest font-extrabold text-gray-300">
              Saúde da operação
            </span>
          </div>
          <span
            data-testid="operation-health-score"
            className={`text-lg font-extrabold ${healthLabel.tone}`}
          >
            {healthScore}%
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-lg leading-none">🚨</span>
          <div className="flex-1 h-3 rounded-full bg-[#0F1219] border border-[#282D3D] overflow-hidden">
            <div
              className={`h-full ${healthLabel.bar} transition-all duration-500`}
              style={{ width: `${healthScore}%` }}
            />
          </div>
          <span aria-hidden className="text-lg leading-none">💰</span>
        </div>
        <p className="text-[11px] text-gray-400 mt-1.5 leading-snug">
          <strong className={`${healthLabel.tone} font-extrabold`}>
            {healthLabel.icon} {healthLabel.label}
          </strong>{" "}
          — {healthDescription}
        </p>
      </div>

      {/* 🧠 Conselheiro da Caverna — Gemini-powered recommendation */}
      <CounselorCard fallbackAction={operation?.next_best_action} />

      {/* 📚 Minha trilha — favourite + in-progress lessons */}
      <LessonTrailCard />

      {/* Compact header — streaks / xp / mode */}
      <div className="rounded-xl border border-[#282D3D] bg-[#14161C] p-4 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-extrabold text-white truncate">
              Jornada de {firstName}
            </h1>
            <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-400">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              <span className="font-semibold text-gray-300">
                {isLive ? "Modo Live (Tráfego Ativo)" : "Modo Caverna (Preparação)"}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            <span
              data-testid="journey-header-streak"
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#1D212C] border border-[#282D3D] text-[11px]"
            >
              <Flame className="w-3.5 h-3.5 text-orange-500 fill-orange-500" />
              <span className="font-extrabold text-white">
                {operation?.operational_streak_days || 1}d
              </span>
            </span>
            <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#1D212C] border border-[#282D3D] text-[11px]">
              <Zap className="w-3.5 h-3.5 text-[#FF6B00] fill-[#FF6B00]" />
              <span className="font-extrabold text-[#FF6B00]">
                {operation?.xp || 0}
              </span>
            </span>
          </div>
        </div>

        {/* Next-best-action / current step name */}
        {currentStep && (
          <div className="mt-3 p-2.5 rounded-lg bg-[#0F1219] border border-[#FF6B00]/25 text-xs">
            <span className="text-[10px] uppercase tracking-wider font-extrabold text-[#FF6B00]">
              Próximo passo
            </span>
            <p className="mt-0.5 text-white font-semibold leading-snug">
              {currentStep.name}
            </p>
          </div>
        )}

        <Link
          to="/operacao"
          data-testid="journey-view-full-op"
          className="mt-2 text-[11px] font-semibold text-gray-400 hover:text-[#FF6B00] flex items-center justify-end gap-0.5"
        >
          Ver painel completo <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Section header */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-widest font-extrabold text-gray-400">
          Modo Caverna — Preparação
        </span>
        <span className="flex-1 h-px bg-[#282D3D]" />
      </div>

      {/* Caverna steps */}
      <div>
        {cavernaSteps.map((step, idx) => (
          <StepNode
            key={step.id}
            step={step}
            isFirst={idx === 0}
            isLast={false}
            expanded={expandedId === step.id}
            onExpand={setExpandedId}
            onToggleItem={handleToggle}
            onOpenVideo={setVideoStep}
            saving={saving}
          />
        ))}
      </div>

      {/* Portal */}
      {state?.portal && <Portal portal={state.portal} />}

      {/* Section header */}
      <div className="flex items-center gap-2 mb-3 mt-2">
        <span className="text-[10px] uppercase tracking-widest font-extrabold text-purple-300">
          Modo Live — Campanhas Ativas
        </span>
        <span className="flex-1 h-px bg-purple-500/20" />
      </div>

      {/* Live steps */}
      <div>
        {liveSteps.map((step, idx) => (
          <StepNode
            key={step.id}
            step={step}
            isFirst={false}
            isLast={idx === liveSteps.length - 1}
            expanded={expandedId === step.id}
            onExpand={setExpandedId}
            onToggleItem={handleToggle}
            onOpenVideo={setVideoStep}
            saving={saving}
          />
        ))}
      </div>

      {/* Floating action buttons for live mode */}
      {isLive && (
        <div className="fixed bottom-16 md:bottom-6 right-4 z-40 flex flex-col gap-2.5">
          <button
            data-testid="journey-fab-register-sale"
            onClick={() => setShowSaleModal(true)}
            className="flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-black font-extrabold text-xs uppercase tracking-wider shadow-[0_4px_25px_rgba(16,185,129,0.5)] transition-transform active:scale-95"
          >
            <DollarSign className="w-4 h-4 stroke-[3]" />
            <span>+ Venda</span>
          </button>
          <button
            data-testid="journey-fab-register-lead"
            onClick={() => setShowLeadModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1F2432] hover:bg-[#282F42] border border-[#3E4760] text-white font-bold text-xs shadow-[0_4px_20px_rgba(0,0,0,0.6)] transition-transform active:scale-95"
          >
            <MessageSquare className="w-3.5 h-3.5 text-[#FF6B00]" />
            <span>+ Lead</span>
          </button>
          <button
            data-testid="journey-fab-register-spend"
            onClick={() => setShowSpendModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1F2432] hover:bg-[#282F42] border border-[#3E4760] text-white font-bold text-xs shadow-[0_4px_20px_rgba(0,0,0,0.6)] transition-transform active:scale-95"
          >
            <DollarSign className="w-3.5 h-3.5 text-amber-400" />
            <span>+ Gasto</span>
          </button>
        </div>
      )}

      {/* First-active welcome */}
      {showWelcome && !state?.access_denied && (
        <div
          data-testid="journey-welcome-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <div className="w-full max-w-md bg-[#14161C] border border-[#FF6B00]/40 rounded-2xl p-6 text-center space-y-3">
            <div className="text-4xl">🎉</div>
            <h2 className="text-lg sm:text-xl font-extrabold text-white">
              Seu acesso foi liberado!
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 leading-snug">
              Bem-vindo à sua mentoria. Vamos começar pelo primeiro passo da sua jornada.
            </p>
            <button
              data-testid="journey-welcome-cta"
              onClick={() => {
                setShowWelcome(false);
                if (currentStep) setExpandedId(currentStep.id);
              }}
              className="w-full py-3 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
            >
              Começar minha jornada
            </button>
          </div>
        </div>
      )}

      <RegisterSaleModal
        isOpen={showSaleModal}
        onClose={() => setShowSaleModal(false)}
        onSaleSuccess={fetchAll}
        activeCampaignId={campaign?._id || campaign?.id}
        preselectedCreativeId={campaign?.creative_id}
      />
      <RegisterLeadModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onLeadSuccess={fetchAll}
        activeCampaignId={campaign?._id || campaign?.id}
      />
      <RegisterSpendModal
        isOpen={showSpendModal}
        onClose={() => setShowSpendModal(false)}
        onSuccess={fetchAll}
      />

      {/* Instructional video modal */}
      {videoStep && (
        <div
          data-testid="journey-video-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setVideoStep(null)}
        >
          <div
            className="w-full max-w-2xl bg-[#0F1219] rounded-2xl border border-[#282D3D] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-[#282D3D]">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-extrabold text-purple-300">
                  🎥 Conteúdo desta etapa
                </p>
                <h3 className="text-sm font-extrabold text-white truncate">
                  {videoStep.instruction_video_title || videoStep.name}
                </h3>
              </div>
              <button
                data-testid="journey-video-close"
                onClick={() => setVideoStep(null)}
                className="px-3 py-1 rounded bg-[#1F2330] hover:bg-[#282F42] text-xs font-bold text-white"
              >
                Fechar
              </button>
            </div>
            {(() => {
              const url = videoStep.instruction_video_url || videoStep.instruction_video_storage_path;
              const src = videoStep.instruction_video_source_type;
              if (!url) return <div className="p-6 text-sm text-gray-400">Sem vídeo disponível.</div>;
              const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
              const vimeo = url.match(/vimeo\.com\/(\d+)/);
              if (src === "google_drive" || src === "dropbox" || /drive\.google\.com|dropbox\.com/.test(url)) {
                return (
                  <div className="p-6 text-center space-y-3">
                    <p className="text-sm text-gray-300">
                      Este vídeo abre no {src === "dropbox" ? "Dropbox" : "Google Drive"}.
                    </p>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="journey-video-external-link"
                      className="inline-block px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-black font-extrabold text-xs uppercase tracking-wider"
                    >
                      Abrir em nova aba
                    </a>
                  </div>
                );
              }
              if (yt) {
                return (
                  <iframe
                    title="Vídeo da etapa"
                    src={`https://www.youtube.com/embed/${yt[1]}?rel=0`}
                    loading="lazy"
                    className="aspect-video border-0"
                    style={{ width: 1, minWidth: "100%" }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                );
              }
              if (vimeo) {
                return (
                  <iframe
                    title="Vídeo da etapa"
                    src={`https://player.vimeo.com/video/${vimeo[1]}`}
                    loading="lazy"
                    className="aspect-video border-0"
                    style={{ width: 1, minWidth: "100%" }}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                );
              }
              return (
                <video controls preload="none" className="w-full aspect-video bg-black" src={url}>
                  Seu navegador não suporta o player de vídeo.
                </video>
              );
            })()}
            {videoStep.instruction_video_description && (
              <p className="text-xs text-gray-300 p-3">{videoStep.instruction_video_description}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
